import classNames from 'classnames';
import moment from 'moment';
import { IconSizes, IconVariants } from '../components/foundations/Icon';
import { Icon } from '../components/widgets';
import TableCellActions from '../components/widgets/TableCellActions';
import TableCellText from '../components/widgets/TableCellText';
import PermissionsCell from './components/table/PermissionsCell';
import AppsCell from './components/table/AppsCell';
import SourcesCell from './components/table/SourcesCell';
import GroupHeaderCell from './components/table/GroupHeaderCell';
import UserTypeCell from './components/table/UserTypeCell';
import css from './styles.css';
import { MEMBER_GROUP_TYPES, MEMBER_TYPES, translationPrefix } from './utils';

const translationTablePrefix = `${translationPrefix}.TABLE_COLUMNS`;
const translationActionPrefix = `${translationPrefix}.ACTIONS`;

const getActionComponentsConfigs = (memberType, onManagePermissions, onDelete, onResendInvitation, translate) => {
    const commonActionComponentProps = {
        size: IconSizes.SMALL,
        variant: IconVariants.LIGHT,
    };

    const shouldShowResendInvitation = row => {
        if (memberType === MEMBER_TYPES.USER) {
            return [MEMBER_GROUP_TYPES.inactive, MEMBER_GROUP_TYPES.pending].includes(row.groupName);
        }
        return false;
    };

    const shouldShowManagePermissions = row => {
        if (memberType === MEMBER_TYPES.USER) {
            return row.groupName === MEMBER_GROUP_TYPES.active;
        }
        return true;
    };

    return [
        {
            ActionComponent: Icon,
            key: 'deleteMember',
            title: translate(`${translationActionPrefix}.DELETE`),
            tooltipText: translate(`${translationActionPrefix}.DELETE`),
            onClick: onDelete,
            shouldShow: row => row.deletable && !row.isMe,
            actionComponentProps: {
                ...commonActionComponentProps,
                name: 'trash',
            },
        },
        {
            ActionComponent: Icon,
            key: 'resendInvitation',
            title: translate(`${translationPrefix}.RESEND_INVITE_REACT`),
            tooltipText: translate(`${translationPrefix}.RESEND_INVITE_REACT`),
            onClick: onResendInvitation,
            shouldShow: shouldShowResendInvitation,
            actionComponentProps: {
                ...commonActionComponentProps,
                name: 'mail-share',
            },
            getActionComponentProps: row => ({
                disabled: row.resendDisabled,
            }),
        },
        {
            ActionComponent: Icon,
            key: 'managePermissions',
            title: translate(`${translationActionPrefix}.MANAGE_PERMISSIONS`),
            tooltipText: translate(`${translationActionPrefix}.MANAGE_PERMISSIONS`),
            onClick: onManagePermissions,
            shouldShow: shouldShowManagePermissions,
            actionComponentProps: {
                ...commonActionComponentProps,
                name: 'edit',
            },
        },
    ];
};

const getAppsColumn = (commonColumnConfig, isAppLevel) => {
    const dimension = isAppLevel ? 'app' : 'app_site_id';
    return {
        ...commonColumnConfig,
        displayName: `${translationTablePrefix}.APPS`,
        cellComponent: AppsCell,
        cellProps: { ...commonColumnConfig.cellProps, isAppLevel },
        cellValues: [
            {
                propName: 'ids',
                dataKey: 'role',
                transformation: role => role?.row?.find(r => r.dimension === dimension)?.values || [],
            },
            {
                propName: 'operator',
                dataKey: 'role',
                transformation: role => role?.row?.find(r => r.dimension === dimension)?.operator || 'not in',
            },
        ],
    };
};

const getSourcesColumn = commonColumnConfig => ({
    ...commonColumnConfig,
    displayName: `${translationTablePrefix}.SOURCES`,
    cellComponent: SourcesCell,
    cellValues: [
        {
            propName: 'selectedSourceNames',
            dataKey: 'role',
            transformation: role => role?.row?.find(r => r.dimension === 'base_source')?.values || [],
        },
        {
            propName: 'operator',
            dataKey: 'role',
            transformation: role => role?.row?.find(r => r.dimension === 'base_source')?.operator || 'not in',
        },
    ],
});

const getLastEditedColumn = (commonColumnConfig, sortConfig, onChangeSort) => ({
    ...commonColumnConfig,
    displayName: `${translationTablePrefix}.LAST_EDITED`,
    cellComponent: TableCellText,
    headerWidth: '10%',
    cellValues: [
        {
            propName: 'value',
            dataKey: 'role',
            transformation: role => (role?.modified ? moment(role.modified).format('MMM DD, YYYY') : ''),
        },
    ],
    showHeaderArrow: true,
    headerArrow: sortConfig.name === 'modified' ? sortConfig.direction : null,
    headerClick: () => onChangeSort('modified'),
});

const getEditedByColumn = commonColumnConfig => ({
    ...commonColumnConfig,
    displayName: `${translationTablePrefix}.EDITED_BY`,
    cellComponent: TableCellText,
    cellProps: {
        ...commonColumnConfig.cellProps,
        tdStyle: { wordBreak: 'break-all' },
    },
    cellValues: [
        {
            propName: 'value',
            dataKey: 'role',
            transformation: role => role?.modified_by || '-',
        },
    ],
});

const getNameColumn = (commonColumnConfig, sortConfig, onChangeSort, headerWidth) => ({
    ...commonColumnConfig,
    cellProps: {
        ...commonColumnConfig.cellProps,
        className: classNames(commonColumnConfig.cellProps.className, css.nameCell),
    },
    name: 'name',
    displayName: `${translationTablePrefix}.NAME`,
    cellComponent: TableCellText,
    headerWidth,
    showHeaderArrow: true,
    headerArrow: sortConfig.name === 'name' ? sortConfig.direction : null,
    headerClick: () => onChangeSort('name'),
});

const getUserColumns = (commonColumnConfig, sortConfig, onChangeSort, translate) => {
    const appsColumn = getAppsColumn(commonColumnConfig, true);
    return [
        getNameColumn(commonColumnConfig, sortConfig, onChangeSort, '13%'),
        {
            ...commonColumnConfig,
            cellProps: {
                ...commonColumnConfig.cellProps,
                className: classNames(commonColumnConfig.cellProps.className, css.emailCell),
            },
            name: 'email',
            displayName: `${translationTablePrefix}.EMAIL`,
            cellComponent: TableCellText,
            headerWidth: '15%',
        },
        {
            ...commonColumnConfig,
            name: 'roleText',
            displayName: `${translationTablePrefix}.TYPE`,
            cellComponent: UserTypeCell,
            headerWidth: '7%',
            showHeaderArrow: true,
            headerArrow: sortConfig.name === 'roleText' ? sortConfig.direction : null,
            headerClick: () => onChangeSort('roleText'),
        },
        {
            ...commonColumnConfig,
            name: 'description',
            displayName: `${translationTablePrefix}.DESCRIPTION`,
            cellComponent: TableCellText,
            headerWidth: '10%',
            cellProps: {
                ...commonColumnConfig.cellProps,
                className: classNames(commonColumnConfig.cellProps.className, css.descriptionCell),
            },
            cellValues: [
                {
                    propName: 'value',
                    dataKey: 'description',
                    transformation: description =>
                        description || translate(`${translationPrefix}.REVIEW.NO_DESCRIPTION`),
                },
            ],
        },
        {
            ...appsColumn,
            headerWidth: '13%',
            cellProps: { ...appsColumn.cellProps, maxItemsToDisplay: 3 },
        },
        {
            ...getSourcesColumn(commonColumnConfig),
            headerWidth: '13%',
            cellProps: { ...commonColumnConfig.cellProps, maxItemsToDisplay: 3 },
        },
        getLastEditedColumn(commonColumnConfig, sortConfig, onChangeSort),
        { ...getEditedByColumn(commonColumnConfig), headerWidth: '14%' },
    ];
};

const getAgencyColumns = (commonColumnConfig, sortConfig, onChangeSort) => [
    getNameColumn(commonColumnConfig, sortConfig, onChangeSort, '12%'),
    {
        ...commonColumnConfig,
        name: 'id',
        displayName: `${translationTablePrefix}.ID`,
        cellComponent: TableCellText,
        headerWidth: '8%',
    },
    { ...getAppsColumn(commonColumnConfig, false), headerWidth: '20%' },
    { ...getSourcesColumn(commonColumnConfig), headerWidth: '20%' },
    {
        ...commonColumnConfig,
        displayName: `${translationTablePrefix}.PERMISSIONS`,
        cellComponent: PermissionsCell,
        headerWidth: '10%',
        cellValues: [
            {
                propName: 'sectionPermissions',
                dataKey: 'role',
                transformation: role => role?.section_permissions,
            },
        ],
    },
    getLastEditedColumn(commonColumnConfig, sortConfig, onChangeSort),
    { ...getEditedByColumn(commonColumnConfig), headerWidth: '15%' },
];

const columnsByType = {
    [MEMBER_TYPES.USER]: getUserColumns,
    [MEMBER_TYPES.AGENCY]: getAgencyColumns,
};

export const getTableColumns = ({
    memberType,
    sortConfig,
    onChangeSort,
    onManagePermissions,
    onDelete,
    onResendInvitation,
    pageLoaded,
    translate,
    searchText,
}) => {
    const isLoading = !pageLoaded;
    const commonColumnConfig = {
        boldHeader: true,
        cellProps: {
            className: css.tableCell,
            isLoading,
            searchText,
        },
    };

    const dataColumns = columnsByType[memberType](commonColumnConfig, sortConfig, onChangeSort, translate);

    const columns = [
        ...dataColumns,
        {
            ...commonColumnConfig,
            displayName: `${translationTablePrefix}.ACTIONS`,
            cellComponent: TableCellActions,
            headerWidth: '5%',
            cellProps: {
                ...commonColumnConfig.cellProps,
                className: classNames(commonColumnConfig.cellProps.className, css.actionCell, {
                    [css.actionCellLoader]: isLoading,
                }),
                actionComponentsConfigs: getActionComponentsConfigs(
                    memberType,
                    onManagePermissions,
                    onDelete,
                    onResendInvitation,
                    translate
                ),
            },
        },
    ];

    const visibleColumnCount = columns.length;
    columns.push({
        subHeader: true,
        displayName: '',
        name: 'groupHeader',
        cellComponent: GroupHeaderCell,
        cellProps: {
            colSpan: visibleColumnCount,
            isLoading,
        },
    });

    return columns;
};
